<template>
  <div class="nebRangeSelect"
    v-neb-click-outside="onClickOutside"
  >
    <div class="nebRangeSelect-select"
      @click="toggleShowDropdown"
      :class="{
        placeholder: !valueSelected,
        disabled,
        readOnly,
        open: showDropdown,
        dirty: isDirty
      }"
    >
      <span>{{ valueLabel }}</span>

      <neb-icon name="caretDown"
        height="12px"
        width="12px"
        v-show="clearable ? !readOnly && (!valueSelected || value === defaultValue) : !readOnly"
      ></neb-icon>

      <neb-icon name="x"
        height="12px"
        width="12px"
        v-show="clearable && !readOnly && !disabled && valueSelected && value !== defaultValue"
        @click.native="onClear"
      ></neb-icon>
    </div>

    <div class="nebRangeSelect-dropdown"
      :class="{ 'edge': dropdownEdge }"
      v-if="showDropdown"
      v-loading="dataLoading"
      ref="dropdown"
    >
      <range-dropdown :data="data"
        v-model="value_"
      ></range-dropdown>
    </div>
  </div>
</template>

<script>
import RangeDropdown from './Dropdown'
import NebIcon from '../../Icon'
import { formatInt } from '../../formatting'
import { isEqual } from 'lodash'

export default {
  name: 'NebRangeSelect',

  components: {
    RangeDropdown,
    NebIcon
  },

  props: {
    /**
     * Each item in the data array should be in the following format:
     *
     * ```javascript
     * { left: 123, right: 456, value: 789 }
     * ```
     *
     * - `left`: The lower bound of the bucket.
     * - `right`: The upper bound of the bucket.
     * - `value`: the bucket value.
     *
     * Buckets will be displayed accross the x axis and the value will be
     * displayed on the y axis.
     *
     * It is not necessary to sort this data. Vega will take care of that.
     */
    data: {
      type: [Array, null],
      default: () => []
    },
    value: {
      type: Array,
      default: () => [ 0, 0 ]
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    highlightDirty: {
      type: Boolean,
      default: false
    },
    defaultValue: {
      type: Array,
      default: undefined
    },
    prefix: {
      type: String,
      default: ''
    },
    clearable: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    dataLoading: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    showDropdown: false,
    dropdownEdge: false
  }),

  computed: {
    isDirty () {
      return this.highlightDirty &&
        this.defaultValue !== undefined &&
        !isEqual(this.value, this.defaultValue)
    },
    valueSelected () {
      return this.value.length > 0
    },
    valueLabel () {
      const [ min=0, max=0 ] = this.value_
      const label = this.valueSelected
        ? `${formatInt(Math.round(min))} - ${formatInt(Math.round(max))}`
        : this.placeholder

      return this.prefix ? `${this.prefix}: ${label}` : label
    },
    value_: {
      get () { return this.value },
      set (v) {
        this.toggleShowDropdown()
        this.$emit('input', v)
      }
    }
  },

  methods: {
    toggleShowDropdown () {
      if (this.disabled || this.readOnly) { return }
      this.$emit('activate')
      this.showDropdown = !this.showDropdown

      if (this.showDropdown) {
        this.$nextTick(this.checkDropdownEdge)
      }
    },
    onClear (e) {
      e.stopPropagation()
      e.preventDefault()

      this.$emit('clear')
    },
    checkDropdownEdge () {
      if (!this.$refs.dropdown) { return }

      const windowWidth = window.innerWidth
      const dropdownRect = this.$refs.dropdown.getBoundingClientRect()

      if (dropdownRect.right >= windowWidth) {
        this.dropdownEdge = true
      }
    },
    onClickOutside () {
      this.showDropdown = false
    }
  }
}
</script>

<style lang="scss">
@import '../../style.scss';

.nebRangeSelect {
  height: 34px;
  position: relative;
  width: 100%;

  &-select {
    height: 34px;
    background-color: $white;
    border-radius: 3px;
    border: solid 1px $light-grey-blue;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding-left: 15px;
    padding-right: 15px;
    padding-top: 1px;
    color: $slate-grey;
    font-family: $defaultFont;
    font-size: 12px;
    cursor: pointer;
    position: relative;

    span {
      margin-right: 10px;
      white-space: nowrap;
      overflow: hidden;
    }

    .nebIcon {
      color: $slate-grey;
      opacity: 0.7;
    }

    &.placeholder {
      color: $slate-grey;
    }

    &.disabled {
      color: $pinkish-grey;
      background-color: $white-four;
      cursor: not-allowed;

      .nebIcon {
        color: $warm-grey;
      }
    }

    &.readOnly {
      border-top: none;
      border-left: none;
      border-right: none;
      border-bottom: 2px solid $warm-grey;
      cursor: initial;
      border-radius: initial;
    }

    &.open {
      box-shadow: 0 0 3px 0 $tiffany-blue;
      border: solid 1px $tiffany-blue;
    }

    &.dirty {
      background-color: #e8f4fc;
      border: 1px solid #a0c3d2;
    }
  }

  &-dropdown {
    background-color: $white;
    border-radius: 3px;
    border: solid 1px $pinkish-grey;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
    position: absolute;
    top: 39px;
    min-width: 100%;
    box-sizing: border-box;
    overflow: hidden;
    z-index: 2001;
    padding: 20px;

    &.edge {
      right: 0;
    }
  }
}
</style>
